<template>
  <b-modal :visible="value" size="lg" @change="$emit('input', $event)" @show="onShow" @hidden="reset">
    <template v-slot:modal-title>
      <span v-if="cycle">
        {{ cycle.orderItemId }} - {{ cycle.patient }}
        <b-badge pill :variant="statusVariant(displayStatus)" class="ml-2">{{ displayStatus | repeatCycleStatus }}</b-badge>
      </span>
      <span v-else>Repeat cycle</span>
    </template>

    <div v-if="loadingDetail" class="loadingOrders">Loading...</div>

    <div v-else-if="cycle">
      <p v-if="metaLine" class="text-muted">{{ metaLine }}</p>

      <b-card no-body class="mb-3">
        <div class="own-header">
          <strong>{{ medicationTitle }}</strong>
          <small class="text-muted d-block">{{ cycle.issued }} of {{ cycle.total }} repeats issued</small>
        </div>
        <b-card-body>
          <div v-for="(med, index) in medications" :key="index" :class="index ? 'border-top pt-3 mt-3' : ''">
            <strong v-if="medications.length > 1" class="d-block mb-2">{{ medicationName(med) }}</strong>
            <b-row>
              <b-col cols="6" md="3">
                <small class="text-muted text-uppercase font-weight-bold d-block">Quantity</small>
                <span>{{ medicationQuantity(med) }}</span>
              </b-col>
              <b-col cols="6" md="3">
                <small class="text-muted text-uppercase font-weight-bold d-block">Frequency</small>
                <span>{{ medicationFrequency(med) }}</span>
              </b-col>
            </b-row>
          </div>
          <b-row v-if="!medications.length">
            <b-col cols="6" md="3">
              <small class="text-muted text-uppercase font-weight-bold d-block">Quantity</small>
              <span>—</span>
            </b-col>
            <b-col cols="6" md="3">
              <small class="text-muted text-uppercase font-weight-bold d-block">Frequency</small>
              <span>—</span>
            </b-col>
          </b-row>
          <!-- Delivery and next-due are cycle-level, not per-medication -->
          <b-row class="border-top pt-3 mt-3">
            <b-col cols="6" md="3">
              <small class="text-muted text-uppercase font-weight-bold d-block">Delivery</small>
              <span>{{ deliveryLabel }}</span>
            </b-col>
            <b-col cols="6" md="3">
              <small class="text-muted text-uppercase font-weight-bold d-block">Next due</small>
              <span>{{ nextDueLabel }}</span>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-row class="mb-3">
        <b-col cols="12" md="3" class="mb-2 mb-md-0">
          <div class="border border-primary rounded p-2 bg-light h-100">
            <small class="text-muted text-uppercase font-weight-bold d-block">Repeats issued</small>
            <strong class="text-primary">{{ cycle.issued }} of {{ cycle.total }} repeat prescriptions issued</strong>
          </div>
        </b-col>
        <b-col cols="12" md="3" class="mb-2 mb-md-0">
          <div class="border rounded p-2 h-100">
            <small class="text-muted text-uppercase font-weight-bold d-block">Status</small>
            <b-badge pill :variant="statusVariant(displayStatus)">{{ displayStatus | repeatCycleStatus }}</b-badge>
          </div>
        </b-col>
        <b-col cols="12" md="3" class="mb-2 mb-md-0">
          <div class="border rounded p-2 h-100">
            <small class="text-muted text-uppercase font-weight-bold d-block">Payment attempts</small>
            <span>{{ cycle.attempts }}</span>
          </div>
        </b-col>
        <b-col cols="12" md="3">
          <div class="border rounded p-2 h-100">
            <small class="text-muted text-uppercase font-weight-bold d-block">Next scheduled action</small>
            <span>{{ cycle.nextAction }}</span>
          </div>
        </b-col>
      </b-row>

      <b-card no-body class="mb-3">
        <div class="own-header"><strong>Repeat timeline</strong></div>
        <div class="scroll-body">
          <div v-for="entry in timelineRows" :key="entry.flow"
            class="d-flex align-items-center border-bottom px-3 py-2">
            <b-badge pill :variant="entry.issued ? 'success' : 'light'" class="mr-2">{{ entry.flow }}</b-badge>
            <b-badge pill :variant="entry.chipVariant">{{ entry.chipLabel }}</b-badge>
            <small class="ml-auto text-muted">{{ entry.when }}</small>
          </div>
        </div>
      </b-card>

      <b-btn variant="primary" class="btn-block m-1" @click="showPatient = !showPatient"
        :class="showPatient ? 'collapsed' : null" aria-controls="cyclePatientCollapse"
        :aria-expanded="showPatient ? 'true' : 'false'">
        <span v-if="!showPatient">show </span>
        <span v-if="showPatient">hide </span>patient details
      </b-btn>
      <b-collapse id="cyclePatientCollapse" v-model="showPatient">
        <div v-if="loadingPatient" class="loadingOrders">Loading...</div>
        <patient-details v-else-if="patient" :patient="patient" />
      </b-collapse>

      <template v-if="consultationNotesEnabled">
        <b-btn variant="primary" class="btn-block m-1" @click="showNotes = !showNotes"
          :class="showNotes ? 'collapsed' : null" aria-controls="cycleNotesCollapse"
          :aria-expanded="showNotes ? 'true' : 'false'">
          <span v-if="!showNotes">show </span>
          <span v-if="showNotes">hide </span>original consultation notes
        </b-btn>
        <b-collapse id="cycleNotesCollapse" v-model="showNotes">
          <div v-if="loadingNotes" class="loadingOrders">Loading...</div>
          <b-card v-else-if="notesError" body-class="text-muted">
            No consultation notes are available for this order.
          </b-card>
          <b-card v-else-if="gpNote">
            <div class="scroll-body">
              <b-row>
                <b-col v-for="field in noteFields" :key="field.label" cols="12" :md="field.wide ? 12 : 6" class="mb-2">
                  <small class="font-weight-bold d-block mb-1">{{ field.label }}</small>
                  <div class="border rounded p-2 note-box" :class="field.value ? '' : 'text-muted font-italic bg-light'">
                    {{ field.value || 'None given' }}
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-collapse>
      </template>

      <b-btn variant="primary" class="btn-block m-1" @click="showLog = !showLog"
        :class="showLog ? 'collapsed' : null" aria-controls="cycleLogCollapse"
        :aria-expanded="showLog ? 'true' : 'false'">
        <span v-if="!showLog">show </span>
        <span v-if="showLog">hide </span>repeat prescription log
      </b-btn>
      <b-collapse id="cycleLogCollapse" v-model="showLog">
        <b-card no-body>
          <div class="scroll-body">
            <div v-if="!logEvents.length" class="text-muted p-3">No events recorded for this cycle yet.</div>
            <b-row v-for="(event, index) in visibleEvents" :key="index" class="border-bottom mx-0 py-2">
              <b-col cols="12" md="3" class="text-muted"><small>{{ event.when }}</small></b-col>
              <b-col cols="12" md="5">
                <b-badge pill :variant="event.variant" class="mr-1">{{ event.type }}</b-badge>
                <small>{{ event.description }}</small>
              </b-col>
              <b-col cols="8" md="2" class="text-muted"><small>{{ event.actor }}</small></b-col>
              <b-col cols="4" md="2" class="text-md-right"><small>{{ event.amount }}</small></b-col>
            </b-row>
            <div v-if="logEvents.length > eventsShown" class="text-center py-2">
              <b-button variant="outline-secondary" size="sm" @click="eventsShown += 10">Load more</b-button>
            </div>
          </div>
        </b-card>
      </b-collapse>

      <div v-if="recoveryEligible" class="border-top mt-3 pt-3">
        <b-button variant="primary" class="mr-2" :disabled="actionBusy || hasPaymentMethod === false" @click="retryPayment">
          <b-spinner small v-if="actionBusy" class="mr-2"></b-spinner>
          Retry payment now
        </b-button>
        <b-button variant="outline-secondary" :disabled="actionBusy" @click="sendPaymentLink">Send payment update link</b-button>
      </div>

      <div v-if="cancelEligible" class="border-top mt-3 pt-3">
        <p class="text-muted small mb-2">
          Cancel the repeat prescription cycle for this order. The patient is notified by email. This does not cancel other orders on the account.
        </p>
        <b-button variant="outline-danger" :disabled="actionBusy" @click="showCancelModal = true">
          Cancel repeat prescription
        </b-button>
      </div>
    </div>

    <cancel-repeat-prescription-modal
      v-model="showCancelModal"
      :order-item-id="orderItemId"
      :patient-label="cycle && cycle.patient"
      @success="onCancelSuccess"
    />

    <template v-slot:modal-footer>
      <b-button v-if="cancelEligible" variant="outline-danger" class="mr-auto" :disabled="actionBusy" @click="showCancelModal = true">
        Cancel repeat prescription
      </b-button>
      <b-button variant="secondary" @click="$emit('input', false)">Close</b-button>
    </template>
  </b-modal>
</template>

<script>

import moment from 'moment';
import PatientDetails from './PatientDetails';
import CancelRepeatPrescriptionModal from './CancelRepeatPrescriptionModal';
import {
  adminDisplayStatus,
  canCancelRepeatCycle,
  failedAttemptsForCurrentFlow,
  isAwaitingPayment,
  issuedRepeats,
  repeatStatusVariant
} from '../pipes/RepeatCycleStatusPipe';
import PusherService from '../services/PusherService';
import Vue from 'vue';

export default {
  components: {
    PatientDetails,
    CancelRepeatPrescriptionModal
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    orderItemId: {
      type: [Number, String],
      default: null
    },
    cycle: {
      type: Object,
      default: null
    }
  },
  data: function () {
    return {
      detail: null,
      order: null,
      prescriptionDetail: null,
      patient: null,
      gpNote: null,
      loadingDetail: false,
      loadingPatient: false,
      loadingNotes: false,
      notesError: false,
      showPatient: false,
      showNotes: false,
      showLog: false,
      eventsShown: 10,
      actionBusy: false,
      hasPaymentMethod: null,
      paymentListenerUnsubscribe: null,
      showCancelModal: false
    }
  },
  computed: {
    // Admin visibility of consultation notes is pending DPO confirmation
    // (CONVENTIONS.md §10) — the section is compiled out entirely when the flag is off.
    consultationNotesEnabled () {
      return process.env.VUE_APP_REPEAT_CONSULTATION_NOTES === 'true';
    },
    // The detail endpoint may omit the slim list-row fields, so computations run on
    // the merge of the table row's data and the freshly fetched detail.
    item () {
      const base = this.cycle ? {
        orderItemId: this.cycle.orderItemId,
        patientName: this.cycle.patient,
        medicationPRV: this.cycle.medication,
        currentRepeatFlow: this.cycle.currentRepeatFlow,
        totalRepeatFlow: this.cycle.total,
        nextScheduledDate: this.cycle.nextDue,
        processStatus: this.cycle.processStatus
      } : {};
      return Object.assign(base, this.detail || {});
    },
    displayStatus () {
      if (this.detail) {
        return adminDisplayStatus(this.item);
      }
      return this.cycle ? this.cycle.displayStatus : null;
    },
    metaLine () {
      if (!this.cycle) {
        return '';
      }
      const parts = [];
      if (this.cycle.total) {
        parts.push(`Repeat ${Math.min(this.cycle.issued + 1, this.cycle.total)} of ${this.cycle.total}`);
      }
      const reviews = (this.item.clinicianReviewReferences || [])
        .filter(review => String(review.status || '').toUpperCase() === 'REVIEWED' && review.reviewDate);
      if (reviews.length) {
        const latest = reviews[reviews.length - 1];
        parts.push(`Last reviewed ${this.formatDate(latest.reviewDate)}`);
      }
      return parts.join(' · ');
    },
    medications () {
      // The repeat detail no longer serialises the prescription, so the
      // separately fetched admin prescription is the usual source.
      const prescription = this.item.prescription || this.prescriptionDetail;
      return (prescription && prescription.medication) || [];
    },
    medicationTitle () {
      if (this.medications.length > 1) {
        return `${this.medications.length} medications`;
      }
      return this.medications.length
        ? this.medicationName(this.medications[0])
        : (this.item.medicationPRV || '—');
    },
    deliveryLabel () {
      return this.order && this.order.deliveryType ? this.prettify(this.order.deliveryType) : '—';
    },
    nextDueLabel () {
      return this.item.nextScheduledDate
        ? moment(this.item.nextScheduledDate, 'YYYY-MM-DD').format('DD MMM YYYY')
        : '—';
    },
    recoveryEligible () {
      return this.displayStatus === 'PAYMENT_FAILED' || this.item.processStatus === 'PROCESSING' ||
        isAwaitingPayment(this.item);
    },
    cancelEligible () {
      return canCancelRepeatCycle(this.item.processStatus);
    },
    // Real per-flow schedule rows when present; otherwise projected from
    // nextScheduledDate at the backend's one-month interval (same derivation as
    // the doctor portal's repeat-status.util).
    timelineRows () {
      const item = this.item;
      const total = item.totalRepeatFlow || 0;
      if (!total) {
        return [];
      }
      const cancelled = item.processStatus === 'REJECTED' || item.processStatus === 'CANCELED';
      const issued = issuedRepeats(item);
      const anchor = item.nextScheduledDate ? moment(item.nextScheduledDate, 'YYYY-MM-DD') : null;
      const flowDates = {};
      (item.orderRepeatPRSCFlowDetails || []).forEach(detail => {
        const date = moment(detail.scheduledDate, 'YYYY-MM-DD');
        if (date.isValid()) {
          flowDates[detail.flowCount] = date.format('DD MMM YYYY');
        }
      });
      const rows = [];
      for (let flow = 1; flow <= total; flow++) {
        const projected = flowDates[flow] ||
          (anchor && anchor.isValid()
            ? anchor.clone().add(flow - (item.currentRepeatFlow || 1), 'months').format('DD MMM YYYY')
            : '—');
        if (flow <= issued) {
          rows.push({ flow: flow, issued: true, chipVariant: 'success', chipLabel: 'Issued', when: projected });
        } else if (cancelled) {
          rows.push({ flow: flow, issued: false, chipVariant: 'secondary', chipLabel: 'Not generated', when: 'Cycle cancelled' });
        } else if (flow === item.currentRepeatFlow) {
          if (this.displayStatus === 'PAYMENT_FAILED') {
            rows.push({ flow: flow, issued: false, chipVariant: 'danger', chipLabel: 'Payment Failed', when: 'Manual recovery - admin' });
          } else if (item.processStatus === 'PROCESSING' || isAwaitingPayment(item)) {
            const chipLabel = failedAttemptsForCurrentFlow(item) ? 'Payment retrying' : 'Pending Payment';
            rows.push({ flow: flow, issued: false, chipVariant: 'warning', chipLabel: chipLabel, when: `Due ${projected}` });
          } else if (this.displayStatus === 'UPCOMING') {
            rows.push({ flow: flow, issued: false, chipVariant: 'secondary', chipLabel: 'Upcoming', when: `Due ${projected}` });
          } else {
            rows.push({ flow: flow, issued: false, chipVariant: 'info', chipLabel: 'Pending Review', when: `Due ${projected}` });
          }
        } else {
          rows.push({ flow: flow, issued: false, chipVariant: 'secondary', chipLabel: 'Upcoming', when: `Due ${projected}` });
        }
      }
      return rows;
    },
    // There is no event-log endpoint in the backend yet, so the log is synthesized
    // from the payment attempts, flow schedule and clinician review references of
    // the cycle detail. The backend exposes no per-event amounts; card data never
    // appears (CONVENTIONS.md §10).
    logEvents () {
      if (!this.detail) {
        return [];
      }
      const item = this.item;
      const total = item.totalRepeatFlow || 0;
      const events = [];
      (item.paymentAttempts || []).forEach(attempt => {
        const captured = String(attempt.status || '').toUpperCase() === 'CAPTURED';
        const attemptedBy = String(attempt.attemptedBy || '').toUpperCase();
        let attemptLabel;
        let actor;
        if (attemptedBy === 'CLINICIAN') {
          attemptLabel = 'immediate charge on clinician approval';
          actor = 'Clinician';
        } else if (attemptedBy === 'ADMIN') {
          attemptLabel = 'manual charge by admin';
          actor = 'Admin';
        } else {
          attemptLabel = `automatic attempt ${attempt.attemptNo}`;
          actor = 'System';
        }
        events.push({
          ts: attempt.attemptTime,
          type: captured ? 'Payment taken' : 'Charge failed',
          variant: captured ? 'success' : 'danger',
          // The first repeat is charged with the original order, not as a
          // repeat-cycle payment, so it reads as the initial payment.
          description: Number(attempt.repeatFlow) === 1
            ? 'Initial prescription payment'
            : `Repeat ${attempt.repeatFlow} of ${total} - ${attemptLabel}`,
          actor: actor,
          amount: '—'
        });
      });
      const issued = issuedRepeats(item);
      (item.orderRepeatPRSCFlowDetails || []).forEach(flowDetail => {
        if (flowDetail.flowCount <= issued) {
          events.push({
            ts: flowDetail.scheduledDate,
            type: 'Prescription issued',
            variant: 'success',
            description: `Repeat ${flowDetail.flowCount} of ${total} issued · payment confirmed`,
            actor: 'System',
            amount: '—'
          });
        }
      });
      (item.clinicianReviewReferences || []).forEach(review => {
        if (String(review.status || '').toUpperCase() === 'REVIEWED') {
          events.push({
            ts: review.reviewDate,
            type: 'Repeat reviewed',
            variant: 'info',
            description: `Repeat ${review.repeatFlow} of ${total} reviewed by clinician`,
            actor: 'Clinician',
            amount: '—'
          });
        }
      });
      // The backend pads absent cancellation fields with the literal "N/A",
      // so placeholders must not count as a recorded cancellation.
      const hasValue = value => {
        const text = String(value || '').trim();
        return text !== '' && text.toUpperCase() !== 'N/A';
      };
      const isCancelled = item.processStatus === 'CANCELED' || item.processStatus === 'REJECTED';
      if (isCancelled || hasValue(item.cancellationTime) ||
          hasValue(item.cancellationReason) || hasValue(item.cancelBy)) {
        // cancelBy arrives as "ROLE-Name", e.g. "CLINICIAN-Dr. Doct Test".
        const cancelBy = hasValue(item.cancelBy) ? String(item.cancelBy) : '';
        const separatorIndex = cancelBy.indexOf('-');
        const actor = separatorIndex >= 0
          ? cancelBy.slice(separatorIndex + 1).trim()
          : this.prettify(cancelBy);
        events.push({
          ts: hasValue(item.cancellationTime)
            ? String(item.cancellationTime).replace(/\[.*\]$/, '')
            : null,
          type: 'Cycle cancelled',
          variant: 'danger',
          description: hasValue(item.cancellationReason)
            ? `Repeat cycle cancelled - ${item.cancellationReason}`
            : 'Repeat cycle cancelled',
          actor: actor || '—',
          amount: '—'
        });
      }
      events.sort((a, b) => {
        const ta = a.ts ? moment(a.ts).valueOf() : 0;
        const tb = b.ts ? moment(b.ts).valueOf() : 0;
        return tb - ta;
      });
      return events.map(event => {
        event.when = event.ts ? this.formatDateTime(event.ts) : '—';
        return event;
      });
    },
    visibleEvents () {
      return this.logEvents.slice(0, this.eventsShown);
    },
    noteFields () {
      const details = (this.gpNote && this.gpNote.details) || {};
      // Canonical twelve-field consultation notes layout (CONVENTIONS.md §9).
      // Only "Patient provided information" has no field in the doctor-note
      // document; it renders as "None given".
      const fields = [
        ['Relevant past medical history', details.relevantPastMedicalHistory],
        ['Relevant past family history', details.relevantPastFamilyHistory],
        ['Medication history (past & present)', details.medicationHistory],
        ['Allergy information / adverse reactions', details.allergyInformation],
        ['Patient provided information', null],
        ['Presenting complaint', details.presentingComplaint],
        ['Differential diagnosis', details.differentialDiagnosis],
        ['Differential diagnosis notes', details.differentialDiagnosisNotes],
        ['Recommended actions', details.recommendedActions],
        ['Notes for patient record', details.additionalNotes],
        ['Referral details', details.patientReferredDetails],
        ['Examination findings', details.examinationFindings]
      ];
      return fields.map(([label, value]) => {
        return { label: label, value: value || null, wide: !!value && value.length > 70 };
      });
    }
  },
  watch: {
    showPatient (open) {
      if (open && !this.patient && !this.loadingPatient) {
        this.loadPatient();
      }
    },
    showNotes (open) {
      if (open && !this.gpNote && !this.notesError && !this.loadingNotes) {
        this.loadNotes();
      }
    },
    'item.patientId' (patientId) {
      if (patientId) {
        this.checkPaymentMethod(patientId);
      }
    }
  },
  methods: {
    statusVariant: repeatStatusVariant,
    medicationName (med) {
      return (med && med.formulary && (med.formulary.fullName || med.formulary.name)) ||
        this.item.medicationPRV || '—';
    },
    medicationQuantity (med) {
      if (!med || med.quantity === null || med.quantity === undefined) {
        return '—';
      }
      return med.unit ? `${med.quantity} ${med.unit}` : String(med.quantity);
    },
    medicationFrequency (med) {
      return (med && med.dosage) || '—';
    },
    onShow () {
      this.reset();
      this.loadingDetail = true;
      this.$repeatPrescriptionService.getRepeatPrescriptionDetail(this.orderItemId).then(detail => {
        this.detail = detail;
        this.loadingDetail = false;
      }).catch(error => {
        console.error(error);
        this.loadingDetail = false;
        this.$awn.alert('There was an error getting the repeat cycle detail');
      });
      this.$orderService.getOrder(this.orderItemId).then(order => {
        this.order = order;
      }).catch(error => {
        // Delivery type and consultation notes degrade gracefully without the order.
        console.error(error);
      });
      this.$orderService.getPrescription(this.orderItemId).then(prescription => {
        this.prescriptionDetail = prescription;
      }).catch(error => {
        // Quantity and frequency degrade to "—" without the prescription.
        console.error(error);
      });
      if (this.item.patientId) {
        this.checkPaymentMethod(this.item.patientId);
      }
      this.setupPaymentListener();
    },
    // All collapsibles reset to collapsed every time the modal opens (spec §4).
    reset () {
      this.detail = null;
      this.order = null;
      this.prescriptionDetail = null;
      this.patient = null;
      this.gpNote = null;
      this.notesError = false;
      this.showPatient = false;
      this.showNotes = false;
      this.showLog = false;
      this.eventsShown = 10;
      this.actionBusy = false;
      this.hasPaymentMethod = null;
      if (this.paymentListenerUnsubscribe) {
        this.paymentListenerUnsubscribe();
        this.paymentListenerUnsubscribe = null;
      }
      this.showCancelModal = false;
    },
    onCancelSuccess () {
      this.$awn.success('Repeat prescription cancelled. The patient has been notified.');
      this.refresh();
    },
    setupPaymentListener () {
      if (this.paymentListenerUnsubscribe) {
        this.paymentListenerUnsubscribe();
      }
      this.paymentListenerUnsubscribe = PusherService.listen('payment', 'payment-event', (data) => {
        if (data.orderItemId !== this.orderItemId) return;  
        if (String(data.status || '').toUpperCase() === 'CAPTURED') {
          this.actionBusy = false;
          this.$awn.success('Payment successful - refreshing details');
          this.refresh();
        } else {
          this.actionBusy = false;
          this.$awn.alert('Payment failed');
        }
      });
    },
    loadPatient () {
      if (!this.item.patientId) {
        return;
      }
      this.loadingPatient = true;
      this.$patientService.getPatient(this.item.patientId).then(patient => {
        this.patient = patient;
        this.loadingPatient = false;
      }).catch(error => {
        console.error(error);
        this.loadingPatient = false;
        this.$awn.alert('There was an error getting the patient details');
      });
    },
    loadNotes () {
      const appointmentId = this.order && this.order.appointment ? this.order.appointment.id : null;
      if (!appointmentId) {
        this.notesError = true;
        return;
      }
      this.loadingNotes = true;
      this.$orderService.getGPNotes(appointmentId).then(gpNote => {
        this.gpNote = gpNote;
        this.loadingNotes = false;
      }).catch(error => {
        console.error(error);
        this.loadingNotes = false;
        this.notesError = true;
      });
    },
    retryPayment () {
      this.actionBusy = true;
      this.$repeatPrescriptionService.retryPaymentNow(this.orderItemId, this.item.patientId).then(() => {
        this.$awn.success('Manual charge triggered against the saved card - waiting for payment confirmation');
      }).catch(error => {
        console.error(error);
        this.actionBusy = false;
        // The backend explains actionable rejections (no saved card, outside
        // the 7-day review window) — show its message when there is one.
        const message = error.response && error.response.data && error.response.data.message;
        this.$awn.alert(typeof message === 'string' && message
          ? message
          : 'There was an error triggering the payment retry');
      });
    },
    sendPaymentLink () {
      this.actionBusy = true;
      const patientEmail = this.order?.patient.email;
      this.$repeatPrescriptionService.sendPaymentUpdateLink(this.orderItemId, patientEmail, this.item.patientId).then(() => {
        this.actionBusy = false;
        this.$awn.success('Payment update link emailed to the patient');
        this.refresh();
      }).catch(error => {
        console.error(error);
        this.actionBusy = false;
        this.$awn.alert('There was an error sending the payment update link');
      });
    },
    refresh () {
      this.onShow();
      this.$emit('refresh');
    },
    checkPaymentMethod (patientId) {
      Vue.axios.get(`api/public/default-payment-method/${patientId}`)
        .then(response => {
          const paymentMethod = response.data && response.data.data;
          this.hasPaymentMethod = !!(paymentMethod && paymentMethod.paymentMethodId);
        })
        .catch(error => {
          console.error(error);
          this.hasPaymentMethod = false;
        });
    },
    formatDate (value) {
      return moment(value).local().format('DD MMM YYYY');
    },
    formatDateTime (value) {
      return moment(value).local().format('HH:mm - DD/MM/YY');
    },
    prettify (value) {
      return String(value).replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase());
    }
  }
}

</script>

<style lang="scss" scoped>
.scroll-body {
  max-height: 280px;
  overflow-y: auto;
}
.note-box {
  min-height: 42px;
}
</style>
